import React, { useEffect, useRef, useState } from 'react';
import { PhoneCall, Check, X, Volume2 } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';

const TARGET_ROLES = ['secretaire', 'assistante', 'technicien', 'admin'];

let audioCtx = null;

function getAudioContext() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    if (!audioCtx || audioCtx.state === 'closed') audioCtx = new Ctx();
    return audioCtx;
  } catch (e) {
    return null;
  }
}

async function playCallSound() {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    if (ctx.state === 'suspended') await ctx.resume();
    const start = ctx.currentTime + 0.02;
    const beep = (offset, freq, duration, level) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, start + offset);
      gain.gain.setValueAtTime(0.0001, start + offset);
      gain.gain.exponentialRampToValueAtTime(level, start + offset + 0.025);
      gain.gain.setValueAtTime(level, start + offset + duration - 0.035);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + offset + duration);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(start + offset);
      osc.stop(start + offset + duration + 0.02);
    };
    const cycle = (base) => {
      beep(base, 1050, 0.20, 0.58);
      beep(base + 0.24, 1050, 0.20, 0.58);
      beep(base + 0.48, 1050, 0.20, 0.58);
      beep(base + 0.82, 780, 0.26, 0.68);
      beep(base + 1.12, 780, 0.26, 0.68);
    };
    cycle(0);
    cycle(1.55);
  } catch (e) {}
}

function formatTime(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    return '';
  }
}

export default function CallAlert() {
  const { user } = useAuth();
  const { socket } = useSocket();
  const [alerts, setAlerts] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const repeatRef = useRef(null);

  useEffect(() => {
    if (!socket || !user) return;
    if (!TARGET_ROLES.includes(user.role)) return;

    const addEntries = (entries, withSound) => {
      const fresh = (entries || []).filter((entry) =>
        entry && entry.id && !entry.acknowledged_at &&
        !(entry.called_by && user.id && entry.called_by === user.id)
      );
      if (!fresh.length) return;
      setAlerts((prev) => {
        const known = new Set(prev.map((a) => a.id));
        const added = fresh.filter((entry) => !known.has(entry.id)).map((entry) => ({
          id: entry.id,
          name: `${entry.first_name || ''} ${entry.last_name || ''}`.trim() || 'Patient',
          dentist: entry.called_by_name || 'Praticien',
          mode: entry.mode,
          ticket: entry.ticket_number,
          at: entry.called_time || new Date().toISOString(),
        }));
        return added.length ? [...added, ...prev] : prev;
      });
      if (withSound) playCallSound();
    };

    const loadActive = async () => {
      try {
        const { data } = await api.get('/waiting-room', { params: { status: 'called' } });
        addEntries(data?.data, false);
      } catch (e) {}
    };

    const onCalled = (entry) => {
      addEntries([entry], true);
    };

    const onAcknowledged = (entry) => {
      if (!entry || !entry.id) return;
      setAlerts((prev) => prev.filter((a) => a.id !== entry.id));
    };

    socket.on('waiting_room:called', onCalled);
    socket.on('waiting_room:acknowledged', onAcknowledged);
    socket.on('connect', loadActive);
    loadActive();
    return () => {
      socket.off('waiting_room:called', onCalled);
      socket.off('waiting_room:acknowledged', onAcknowledged);
      socket.off('connect', loadActive);
    };
  }, [socket, user]);

  useEffect(() => {
    if (!user || !TARGET_ROLES.includes(user.role) || !alerts.length) return;
    repeatRef.current = setInterval(() => playCallSound(), 10000);
    return () => {
      if (repeatRef.current) clearInterval(repeatRef.current);
      repeatRef.current = null;
    };
  }, [alerts.length, user]);

  const dismiss = (id) => setAlerts((prev) => prev.filter((a) => a.id !== id));

  const acknowledge = async (id) => {
    setBusyId(id);
    try {
      await api.put(`/waiting-room/${id}/acknowledge`);
      dismiss(id);
    } catch (e) {
      dismiss(id);
    } finally {
      setBusyId(null);
    }
  };

  if (!alerts.length) return null;

  return (
    <div style={{ position: 'fixed', top: 70, right: 18, zIndex: 12000, display: 'flex', flexDirection: 'column', gap: 12, width: 'min(360px, calc(100vw - 24px))' }}>
      <style>{`
        @keyframes callPulse { 0%,100%{transform:scale(1);box-shadow:0 0 0 0 rgba(239,68,68,0.5);} 50%{transform:scale(1.06);box-shadow:0 0 0 10px rgba(239,68,68,0);} }
        @keyframes callIn { from{opacity:0;transform:translateX(24px);} to{opacity:1;transform:translateX(0);} }
      `}</style>
      {alerts.map((a) => (
        <div key={a.id} style={{
          background: '#fff', borderRadius: 16, overflow: 'hidden',
          border: '2px solid #ef4444', boxShadow: '0 18px 50px rgba(15,23,42,0.25)',
          animation: 'callIn 0.25s ease-out',
        }}>
          <div style={{ background: 'linear-gradient(135deg,#dc2626,#b91c1c)', color: '#fff', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 38, height: 38, borderRadius: 12, background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'callPulse 1.4s ease-in-out infinite' }}>
              <PhoneCall size={20} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, opacity: 0.9, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Le praticien appelle un patient
              </div>
              <div style={{ fontSize: 16, fontWeight: 900, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {a.name}
              </div>
            </div>
            <button onClick={() => dismiss(a.id)} title="Fermer" style={{ background: 'rgba(255,255,255,0.16)', border: 'none', color: '#fff', width: 30, height: 30, borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <X size={16} />
            </button>
          </div>

          <div style={{ padding: '12px 16px 14px' }}>
            <div style={{ fontSize: 12.5, color: '#334155', lineHeight: 1.6 }}>
              <div><b>{a.dentist}</b> demande de faire entrer ce patient.</div>
              <div style={{ color: '#64748b', marginTop: 3 }}>
                {a.mode === 'walk_in' ? `Sans RDV${a.ticket ? ` • Ticket #${a.ticket}` : ''}` : 'Avec RDV'}
                {a.at ? ` • Appelé à ${formatTime(a.at)}` : ''}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button
                onClick={() => acknowledge(a.id)}
                disabled={busyId === a.id}
                style={{
                  flex: 1, padding: '10px 12px', borderRadius: 10, border: 'none',
                  background: busyId === a.id ? '#e2e8f0' : 'linear-gradient(135deg,#16a34a,#15803d)',
                  color: busyId === a.id ? '#94a3b8' : '#fff', fontSize: 13, fontWeight: 800,
                  cursor: busyId === a.id ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
              >
                <Check size={15} /> J'ai fait entrer
              </button>
              <button
                onClick={() => dismiss(a.id)}
                style={{
                  padding: '10px 12px', borderRadius: 10, border: '1px solid #e2e8f0',
                  background: '#f8fafc', color: '#64748b', fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
                }}
              >
                Plus tard
              </button>
            </div>
            <div style={{ marginTop: 8, fontSize: 10.5, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 5 }}>
              <Volume2 size={12} /> Alerte sonore activée
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}